// The iOS push for the Reason -> Log Job hand-off (KAN-765). Log Job is its own
// route, so its BODY (everything under the shared header; the header never
// moves) enters with the same push FullScreenStepPager uses: translateX(100%)
// -> 0, 320ms, cubic-bezier(.2,.8,.2,1). It is a CSS animation with fill-mode
// `backwards` only, so the transform is `none` at rest (no containing block for
// fixed children afterwards) and nothing uses will-change. Reduced motion = a
// 150ms fade. Keyframes are injected once, lazily (same pattern as LogJobSkeleton).
const STYLE_ID = 'cvx-uv-push-keyframes';

function ensureKeyframes() {
  if (typeof document === 'undefined' || document.getElementById(STYLE_ID)) return;
  const s = document.createElement('style');
  s.id = STYLE_ID;
  s.textContent = '@keyframes cvxUvPushIn{from{transform:translateX(100%)}to{transform:none}}'
    + '@keyframes cvxUvFadeIn{from{opacity:0}to{opacity:1}}';
  document.head.appendChild(s);
}

export const UNSCHEDULED_PUSH_MS = 320;
export const UNSCHEDULED_PUSH_FADE_MS = 150;

/** The CSS `animation` shorthand for the Log Job body's entrance. */
export function unscheduledPushAnimation(reduced) {
  ensureKeyframes();
  return reduced
    ? `cvxUvFadeIn ${UNSCHEDULED_PUSH_FADE_MS}ms linear backwards`
    : `cvxUvPushIn ${UNSCHEDULED_PUSH_MS}ms cubic-bezier(.2,.8,.2,1) backwards`;
}

/** The wrapper style: fills the page under the header, so its layout is the page's own. */
export function unscheduledBodyStyle(animation) {
  return {
    flex: 1, minHeight: 0, width: '100%', display: 'flex', flexDirection: 'column', ...(animation ? { animation } : null),
  };
}

/**
 * Reduced-motion preference read SYNCHRONOUSLY. The push is latched once, at the
 * first render (a changing `animation` value would restart it), and the house
 * hook only learns the preference in an effect, a tick too late for that.
 */
export function readReducedMotionNow() {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export const UNSCHEDULED_SWAP_FADE_MS = 120;

/**
 * Style of the box INSIDE the moving wrapper that holds the skeleton / the form.
 * When the form replaces the skeleton it crossfades in (opacity only, 120ms) --
 * the wrapper's push is never restarted or cut, and the form adds no rise.
 */
export function unscheduledSwapStyle(fadeIn) {
  ensureKeyframes();
  return {
    flex: 1, minHeight: 0, width: '100%', display: 'flex', flexDirection: 'column',
    ...(fadeIn ? { animation: `cvxUvFadeIn ${UNSCHEDULED_SWAP_FADE_MS}ms linear backwards` } : null),
  };
}

/**
 * Desktop arrival (KAN-771): ONLY the form pane moves; the rail is at rest from
 * the first frame, in the rect the Reason step's rail occupied. The pane gets
 * the latched push on the skeleton or on a form that is the first thing
 * painted, a 120ms fade when the form replaces a painted skeleton, and sits
 * above nothing (the rail is raised over it in LogJob so a sliding pane passes
 * UNDER the rail).
 *
 * @param {'skeleton'|'form-fade'|'form'} phase
 * @param {string} animation - the latched `unscheduledPushAnimation(...)` value.
 */
export function unscheduledPaneStyle(phase, animation) {
  ensureKeyframes();
  if (phase === 'form-fade') return { animation: `cvxUvFadeIn ${UNSCHEDULED_SWAP_FADE_MS}ms linear backwards` };
  return { position: 'relative', zIndex: 0, animation };
}

/** Raises the rail above a sliding form pane (both are positioned, so z-index decides). */
export const UNSCHEDULED_RAIL_RAISED = { position: 'relative', zIndex: 1 };
